<template>
  <b-modal
    ref="modal"
    :id="modalId"
    :title="isEdit ? 'Update Notification Event' : isView ? 'View Notification Event' : 'Create Notification Event'"
    :ok-title="isView ? null : 'Save'"
    cancel-title="Cancel"
    @show="resetModal"
    @hidden="onHidden"
    @ok="handleOk"
    centered
    size="lg"
    :hide-footer="isView"
  >
    <b-form ref="form" @submit.stop.prevent="handleSubmit">
      <b-row>
        <b-col cols="6">
          <b-form-group label="Trigger" :state="getState('notification_trigger_id')">
            <v-select
              v-model="form.notification_trigger_id"
              :options="triggerOptions"
              :reduce="(e) => e.id"
              label="name"
              placeholder="Select Trigger"
              :class="{ 'is-invalid': formErrors.notification_trigger_id }"
              required
              :disabled="isView"
            />
            <b-form-invalid-feedback v-if="formErrors.notification_trigger_id">{{
              formErrors.notification_trigger_id
            }}</b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label="Name"
            label-for="name-input"
            :state="getState('name')"
            invalid-feedback="Name is required and must be less than 255 characters."
          >
            <b-form-input
              id="name-input"
              v-model="form.name"
              :state="getState('name')"
              placeholder="Name"
              required
              maxlength="255"
              :disabled="isView"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="6">
          <b-form-group label="Tenant" :state="getState('tenant')">
            <v-select
              v-model="form.tenant"
              :options="tenantOptions"
              :reduce="(e) => e.id"
              label="name"
              placeholder="Select Tenant"
              :class="{ 'is-invalid': formErrors.tenant }"
              required
              :disabled="isView"
            />
            <b-form-invalid-feedback v-if="formErrors.tenant">{{ formErrors.tenant }}</b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group label="Recipients" :state="getState('recipients')">
            <v-select
              v-model="form.recipients"
              :options="recipientOptions"
              :reduce="(e) => e.id"
              label="name"
              placeholder="Select Recipients"
              multiple
              :class="{ 'is-invalid': formErrors.recipients }"
              required
              :disabled="isView"
            />
            <b-form-invalid-feedback v-if="formErrors.recipients">{{ formErrors.recipients }}</b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Default Variables">
        <b-row v-for="(item, index) in defaultVariablesList" :key="index" class="mb-4">
          <b-col cols="6">
            <b-form-input v-model="item.key" placeholder="Key (e.g. email)" :disabled="isView" />
          </b-col>
          <b-col cols="6" class="d-flex align-items-center gap-3">
            <b-form-input v-model="item.value" placeholder="Value (e.g. user@example.com)" :disabled="isView" />
            <b-button v-if="!isView" variant="danger" size="sm" @click="removeVariable(index)" title="Remove">
              <i class="fe fe-trash"></i>
            </b-button>
          </b-col>
        </b-row>
        <b-button v-if="!isView" variant="primary" size="sm" @click="addVariable">
          <i class="fe fe-plus"></i> Add Field
        </b-button>
      </b-form-group>

      <b-form-group label="Description">
        <b-form-textarea rows="2" v-model="form.description" placeholder="Description" :disabled="isView" />
      </b-form-group>

      <b-row>
        <b-col cols="6">
          <b-form-group label="Channels">
            <div class="d-flex flex-wrap gap-3">
              <b-form-checkbox v-model="form.channels.whatsapp" :disabled="isView">WhatsApp</b-form-checkbox>
              <b-form-checkbox v-model="form.channels['in-web']" :disabled="isView">In-Web</b-form-checkbox>
              <b-form-checkbox v-model="form.channels.email" :disabled="isView">Email</b-form-checkbox>
            </div>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
  </b-modal>
</template>

<script>
export default {
  name: "NotificationEventModal",
  props: {
    modalId: { type: String, default: "notification-event-modal" },
    isEdit: { type: Boolean, default: false },
    isView: { type: Boolean, default: false },
    tenantOptions: { type: Array, default: () => [] },
    triggerOptions: { type: Array, default: () => [] },
    recipientOptions: { type: Array, default: () => [] },
    data: { type: Object, default: null },
  },

  data() {
    return {
      form: this.getInitialForm(),
      defaultVariablesList: [{ key: "", value: "" }],
      formErrors: {},
      suppressValidation: false,
    };
  },

  watch: {
    isEdit(val) {
      if (!val) {
        this.form = this.getInitialForm();
        this.defaultVariablesList = [{ key: "", value: "" }];
        this.formErrors = {};
      } else {
        if (this.data) this.populateFromData(this.data);
      }
    },

    data: {
      deep: true,
      handler(val) {
        if ((this.isEdit || this.isView) && val) {
          this.populateFromData(val);
        }
      },
    },

    defaultVariablesList: {
      deep: true,
      handler(newList) {
        const obj = {};
        newList.forEach(({ key, value }) => {
          if (key) obj[key] = value;
        });
        this.form.default_variables = obj;
      },
    },

    "form.notification_trigger_id"(val) {
      if (this.suppressValidation) return;
      if (!val) this.formErrors.notification_trigger_id = "Trigger is required.";
      else delete this.formErrors.notification_trigger_id;
    },

    "form.name"(val) {
      if (this.suppressValidation) return;
      if (!val) this.formErrors.name = "Name is required.";
      else if (val.length > 255) this.formErrors.name = "Name must be less than 255 characters.";
      else delete this.formErrors.name;
    },

    "form.tenant"(val) {
      if (this.suppressValidation) return;
      if (!val) this.formErrors.tenant = "Tenant is required.";
      else delete this.formErrors.tenant;
    },
  },

  methods: {
    getInitialForm() {
      return {
        notification_trigger_id: null,
        name: "",
        tenant: null,
        recipients: [],
        description: "",
        channels: { whatsapp: false, "in-web": false, email: false },
        default_variables: {},
      };
    },

    populateFromData(d) {
      this.form = {
        notification_trigger_id: d.notification_trigger_id || null,
        name: d.name || "",
        tenant: d.tenant || null,
        recipients: Array.isArray(d.recipients) ? d.recipients : [],
        description: d.description || "",
        channels: d.channels || { whatsapp: false, "in-web": false, email: false },
        default_variables: d.default_variables || {},
      };

      this.defaultVariablesList = Object.entries(d.default_variables || {}).map(([key, value]) => ({ key, value }));

      if (this.defaultVariablesList.length === 0) this.defaultVariablesList = [{ key: "", value: "" }];

      this.formErrors = {};
    },

    addVariable() {
      this.defaultVariablesList.push({ key: "", value: "" });
    },

    removeVariable(index) {
      this.defaultVariablesList.splice(index, 1);
    },

    getState(field) {
      if (this.formErrors[field]) return false;
      return null;
    },

    validateAll() {
      this.formErrors = {};

      if (!this.form.notification_trigger_id) this.formErrors.notification_trigger_id = "Trigger is required.";

      if (!this.form.name) this.formErrors.name = "Name is required.";
      else if (this.form.name.length > 255) this.formErrors.name = "Name must be less than 255 characters.";

      if (!this.form.tenant) this.formErrors.tenant = "Tenant is required.";

      return Object.keys(this.formErrors).length === 0;
    },

    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },

    handleSubmit() {
      const isValid = this.validateAll();
      if (!isValid) return;

      const payload = { ...this.form };
      this.$emit("save", payload);
    },

    async resetModal() {
      this.suppressValidation = true;

      await this.$nextTick();

      if ((this.isEdit || this.isView) && this.data) {
        this.populateFromData(this.data);
      } else {
        this.form = this.getInitialForm();
        this.defaultVariablesList = [{ key: "", value: "" }];
        this.formErrors = {};
      }

      this.$nextTick(() => {
        this.suppressValidation = false;
      });
    },

    onHidden() {
      this.resetModal();
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.w-50 {
  width: 50%;
}
</style>
